import { useEffect, useRef, useState } from "react";

import { SessionContextProps } from "../../../../Context/SessionContext";
import { UserContextProps } from "../../../../Context/UserContext";
import { CustomDimension } from "../../../../Models/CustomDimension";
import { LeadResultType } from "../../../../Utils/Enums";
import SessionContextValueProvider from "../../../Providers/Value/SessionContextValueProvider";
import UserContextValueProvider from "../../../Providers/Value/UserContextValueProvider";
import DispositionStep, { IDispositionStep } from "../Components/DispositionStep";
import { IDispositionScenarioSessionProps } from "../Types/DispositionScenario";
import DispositionScenarioController from "../Types/DispositionScenarioController";
import {
  BookedAppointmentScenario,
  CustomerDisqualifiedScenario,
  InvalidCustomerPropertiesScenario,
  NoBookScenario
} from "./DispositionScenarios";
import SessionContextBookingErrorConfirmation from "./SessionContextBookingErrorConfirmation";
import SessionContextCustomerInvalidDispositionAlert from "./SessionContextCustomerInvalidDispositionAlert";
import SessionContextDuplicatesDispositionAlert from "./SessionContextDuplicatesDispositionAlert";
import SessionContextPhoneNumberDispositionErrorAlert from "./SessionContextPhoneNumberDispositionErrorAlert";
import UserSessionContextDispositionModal from "./UserSessionContextDispositionModal";

const blockingAlerts: IDispositionStep["blockingAlerts"] = [SessionContextPhoneNumberDispositionErrorAlert];

const alerts: IDispositionStep["alerts"] = [
  SessionContextDuplicatesDispositionAlert,
  SessionContextCustomerInvalidDispositionAlert
];

const dispositionScenarioController = new DispositionScenarioController([
  InvalidCustomerPropertiesScenario,
  CustomerDisqualifiedScenario,
  BookedAppointmentScenario,
  NoBookScenario
]);

type DispositionScenarioUserProps = Pick<UserContextProps, "disqualifyingReasons" | "leadResultTypes">;

interface ISessionContextDispositionStep
  extends IDispositionScenarioSessionProps,
    Pick<
      SessionContextProps,
      "apptErrorCenters" | "isSavingCustomer" | "leadResult" | "setSaveCustomer" | "setIsSessionShortCircuited"
    >,
    DispositionScenarioUserProps {
  appointment: SessionContextProps["appointment"];
  scenarioController: DispositionScenarioController;
  selectedCustomer: SessionContextProps["selectedCustomer"];
}

/** A disposition step which is controlled by current application properties and a configuration of scenarios which
 * filter the allowed disposition options, and alerts which might be displayed to the user for information or
 * conflict resolution purposes.
 */
const ControlledDispositionStep: React.FC<ISessionContextDispositionStep> = ({
  apptErrorCenters,
  appointment,
  customer,
  disqualifyingReasons,
  isLeadManualOutbound,
  isSessionShortCircuited,
  isSavingCustomer,
  lead,
  leadResultTypes,
  leadDisqualifiers,
  leadResult,
  scenarioController,
  selectedCustomer,
  setIsSessionShortCircuited,
  setSaveCustomer
}) => {
  const [isBookingManuallyConfirmed, setIsBookingManuallyConfirmed] = useState<boolean | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [leadResultTypeOptions, setLeadResultOptions] = useState<CustomDimension<number>[]>([]);
  const [selectedLeadResultType, setSelectedLeadResultType] = useState(
    leadResultTypes.find(({ id }) => id === leadResult?.type) || null
  );

  const customerPropsRef = useRef({ id: customer.id, opportunityId: customer.opportunity?.id });

  useEffect(() => {
    const scenarioProps = {
      appointment,
      customer,
      disqualifyingReasons,
      isBookingManuallyConfirmed: isBookingManuallyConfirmed || false,
      isLeadManualOutbound,
      isSessionShortCircuited,
      lead,
      leadDisqualifiers,
      leadResultTypes,
      selectedCustomer
    };
    setLeadResultOptions(
      scenarioController
        .getScenario(scenarioProps)
        .optionsFilter.getAllowedLeadResultTypes(scenarioProps, leadResultTypes)
    );
  }, [
    appointment,
    isBookingManuallyConfirmed,
    customer,
    disqualifyingReasons,
    isLeadManualOutbound,
    isSessionShortCircuited,
    lead,
    leadDisqualifiers,
    leadResultTypes,
    selectedCustomer,
    setIsSessionShortCircuited
  ]);

  // ON CUSTOMER ID CHANGING
  useEffect(() => {
    // open the confirmation modal seamlessly
    if (
      // IF the customer is successfully POSTed
      ((!customerPropsRef.current.id && customer.id) ||
        // OR the customer had a new opportunity assigned to it
        (!customerPropsRef.current.opportunityId && customer.opportunity?.id)) &&
      selectedLeadResultType
    )
      setIsModalOpen(true);
    customerPropsRef.current = { id: customer.id, opportunityId: customer.opportunity?.id };
  }, [customer.id, customer.opportunity?.id, selectedLeadResultType]);

  const handleResultClick = (type: CustomDimension<number> | null) => {
    setSelectedLeadResultType(type);
    if (type) {
      // IF the type is bad lead, skip all verification and proceed
      if (type.name === LeadResultType.BadLead) setIsModalOpen(true);
      // ELSE IF the customer doesn't have an id or opportunity, attempt a save
      else if (!customer.id || !customer.opportunity?.id)
        setSaveCustomer({ override: false, customer, forceUpdate: true });
      // ELSE proceed
      else setIsModalOpen(true);
    }
  };
  return DispositionStep({
    alerts,
    blockingAlerts,
    showBookingErrorConfirmation:
      Boolean(apptErrorCenters.length && !appointment) && isBookingManuallyConfirmed === null,
    isLoading: isSavingCustomer && !customer.id,
    isModalOpen,
    leadResultType: selectedLeadResultType,
    leadResultTypeOptions,
    setLeadResultType: handleResultClick,
    setIsBookingManuallyConfirmed,
    ConfirmationModal: UserSessionContextDispositionModal,
    BookingErrorForm: SessionContextBookingErrorConfirmation
  });
};

const ConfiguredDispositionStep: React.FC<Omit<ISessionContextDispositionStep, "dispositionScenarios">> = props =>
  ControlledDispositionStep({ ...props, scenarioController: dispositionScenarioController });

const SessionContextDispositionStep = SessionContextValueProvider({
  Component: ConfiguredDispositionStep,
  map: ({
    apptErrorCenters,
    appointment,
    customer,
    isLeadManualOutbound,
    isSessionShortCircuited,
    isSavingCustomer,
    lead,
    leadDisqualifiers,
    leadResult,
    selectedCustomer,
    setIsSessionShortCircuited,
    setSaveCustomer
  }) => ({
    apptErrorCenters,
    appointment,
    customer,
    isLeadManualOutbound,
    isSessionShortCircuited,
    isSavingCustomer,
    lead,
    leadDisqualifiers,
    leadResult,
    selectedCustomer,
    setIsSessionShortCircuited,
    setSaveCustomer
  })
});

const UserSessionContextDispositionStep = UserContextValueProvider({
  Component: SessionContextDispositionStep,
  map: ({ leadResultTypes, disqualifyingReasons }) => ({ leadResultTypes, disqualifyingReasons })
});

export default UserSessionContextDispositionStep;
